import type {
  Frequency,
  Fund,
  FundSetup,
  Offer,
  SetupValue,
  Strategy,
  UnitBasis,
  UnitClass,
} from '../../data/types';
import type { SetupField, SetupSection } from './fieldTypes';
import { ACTIVATION_SECTIONS, ALL_FIELDS, OFFER_FORM_SECTIONS, sectionById } from './sections';

type Values = Record<string, SetupValue>;

const text = (values: Values, key: string) => String(values[key] ?? '').trim();
const num = (values: Values, key: string) => Number(text(values, key).replace(/[^\d.]/g, '')) || 0;

export function visibleFields(section: SetupSection, values: Values): SetupField[] {
  return section.blocks
    .flatMap((block) => block.fields)
    .filter((field) => !field.showIf || field.showIf(values));
}

export function fieldError(field: SetupField, value: SetupValue | undefined): string | undefined {
  if (field.kind === 'switch') return undefined;
  const raw = String(value ?? '').trim();
  if (!raw) return field.required ? 'Required' : undefined;
  if (field.pattern && !field.pattern.regex.test(raw)) return field.pattern.message;
  return undefined;
}

export function sectionErrors(sectionId: string, values: Values): Record<string, string> {
  const section = sectionById(sectionId);
  if (!section) return {};
  const errors: Record<string, string> = {};
  for (const field of visibleFields(section, values)) {
    const error = fieldError(field, values[field.key]);
    if (error) errors[field.key] = error;
  }
  if (sectionId === 'amount') {
    const max = num(values, 'offer.max');
    if (max && max < num(values, 'offer.min')) errors['offer.max'] = 'Below the minimum';
  }
  if (
    sectionId === 'amount' &&
    num(values, 'offer.payableNow') > (num(values, 'class.issuePrice') || 1)
  ) {
    errors['offer.payableNow'] =
      `Can't be more than the $${text(values, 'class.issuePrice')} issue price`;
  }
  if (sectionId === 'amount' && !values['offer.payEft'] && !values['offer.payBpay']) {
    errors['offer.payEft'] = 'Choose at least one payment method';
  }
  return errors;
}

export const sectionValid = (sectionId: string, values: Values) =>
  Object.keys(sectionErrors(sectionId, values)).length === 0;

export const canActivate = (setup: FundSetup) =>
  setup.status === 'DRAFT' && ACTIVATION_SECTIONS.every((id) => sectionValid(id, setup.values));

export const canPublish = (setup: FundSetup) =>
  setup.status === 'ACTIVE' && OFFER_FORM_SECTIONS.every((id) => sectionValid(id, setup.values));

export function setupStats(setup: FundSetup) {
  let prefilled = 0;
  let changed = 0;
  for (const field of ALL_FIELDS) {
    const value = setup.values[field.key];
    if (value === undefined || value === '') continue;
    if (value === setup.prefill[field.key]) prefilled += 1;
    else changed += 1;
  }
  return { total: ALL_FIELDS.length, prefilled, changed };
}

export function certificateNumber(values: Values, sequence = num(values, 'cert.startSeq') || 1) {
  return `${text(values, 'cert.companyCode')}-${text(values, 'cert.securityCode')}-${String(sequence).padStart(6, '0')}`;
}

export function buildFund(setup: FundSetup, fundId: string, today: string): Fund {
  const { values } = setup;
  return {
    id: fundId,
    tenantId: setup.tenantId,
    name: text(values, 'fund.name'),
    code: text(values, 'fund.code'),
    strategy: text(values, 'fund.strategy') as Strategy,
    status: 'SETUP',
    basis: text(values, 'fund.basis') as UnitBasis,
    trustee: text(values, 'fund.trustee'),
    abn: text(values, 'fund.abn'),
    inceptionDate: text(values, 'fund.inceptionDate'),
    maturityDate: text(values, 'fund.maturityDate') || undefined,
    distributionFrequency: text(values, 'fund.frequency') as Frequency,
    targetSize: num(values, 'fund.targetSize'),
    bank: {
      bsb: text(values, 'bank.bsb'),
      accountNumber: text(values, 'bank.account'),
      accountName: text(values, 'bank.accountName'),
      verified: false,
    },
    apcaUserId: text(values, 'bank.apca'),
    bpayBillerCode: text(values, 'bank.bpay'),
    registryLiveDate: today,
    description: text(values, 'fund.summary'),
  };
}

export function buildClass(setup: FundSetup, fundId: string, classId: string): UnitClass {
  const { values } = setup;
  const issuePrice = num(values, 'class.issuePrice') || 1;
  return {
    id: classId,
    fundId,
    name: text(values, 'class.name'),
    code: text(values, 'class.code'),
    issuePrice,
    unitPrice: num(values, 'price.mid') || issuePrice,
    targetReturnPct: num(values, 'class.targetReturn'),
    managementFeePct: num(values, 'class.fee'),
    minInvestment: num(values, 'offer.min'),
    open: false,
    paidOnApplicationPct:
      values['class.partlyPaid'] === true ? num(values, 'class.paidOnApplication') : undefined,
    priceHistory: [
      { date: text(values, 'price.date'), price: num(values, 'price.mid') || issuePrice },
    ],
  };
}

export function buildOffer(
  setup: FundSetup,
  offerId: string,
  publishedAt: string,
  publishedBy: string,
): Offer {
  const { values } = setup;
  const lines = (key: string) =>
    text(values, key)
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
  return {
    id: offerId,
    tenantId: setup.tenantId,
    setupId: setup.id,
    fundId: setup.fundId!,
    classId: setup.classId!,
    title: text(values, 'offer.title'),
    status: 'PUBLISHED',
    summary: text(values, 'offer.summary'),
    url: text(values, 'offer.url'),
    closesAt: `${text(values, 'offer.closeDate')}T${text(values, 'offer.closeTime')}`,
    showCountdown: values['offer.countdown'] === true,
    showInInvestorCentre: values['offer.useExisting'] === true,
    publicApply: values['offer.publicApply'] === true,
    issueAtClose: values['offer.issueAtClose'] === true,
    minInvestment: num(values, 'offer.min'),
    maxInvestment: num(values, 'offer.max') || undefined,
    increment: num(values, 'offer.increment'),
    paymentMethods: [
      ...(values['offer.payEft'] ? ['EFT'] : []),
      ...(values['offer.payBpay'] ? ['BPAY'] : []),
    ],
    documents: lines('offer.documents'),
    brandColour: text(values, 'brand.colour'),
    publishedAt,
    publishedBy,
  };
}
